import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import { Points, PointMaterial } from '@react-three/drei';
import { Points as ThreePoints } from 'three';
import { CardGrade } from '../../types/cards';
import { getOptimalParticleCount } from '../../utils/deviceDetection';

interface ParticleSystemProps {
  phase: 'intro' | 'reveal' | 'highlight' | 'complete';
  highestGrade?: CardGrade;
}

// 등급별 파티클 색상 (사막 테마)
const GRADE_PARTICLE_COLORS: Record<CardGrade, string> = {
  SS: '#FFD700', // 황금
  S: '#FF8C42',  // 주황 (사막 일몰)
  A: '#D4AF37',  // 골드
  B: '#C9A961',  // 모래색
  C: '#C97D60',  // 테라코타
  D: '#8B7355',  // 갈색 모래
};

// 구체 내부에 균등하게 분포된 점 생성 함수
function generateSpherePoints(count: number, radius: number): Float32Array {
  const positions = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    // 구면 좌표계를 사용하여 균등 분포
    const u = Math.random();
    const v = Math.random();
    const theta = 2 * Math.PI * u;
    const phi = Math.acos(2 * v - 1);
    const r = radius * Math.cbrt(Math.random()); // 부피 기준 균등 분포
    
    positions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
    positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
    positions[i * 3 + 2] = r * Math.cos(phi);
  }
  return positions;
}

export function ParticleSystem({ phase, highestGrade }: ParticleSystemProps) {
  const pointsRef = useRef<ThreePoints>(null);

  // 등급에 따라 파티클 수와 크기 조정 (모바일 최적화 적용)
  const particleConfig = useMemo(() => {
    if (!highestGrade) {
      const baseCount = 500;
      return { 
        count: getOptimalParticleCount(baseCount), 
        size: 0.05, 
        color: '#d4af37' 
      };
    }
    
    const baseConfigs: Record<CardGrade, { count: number; size: number; color: string }> = {
      SS: { count: 1200, size: 0.08, color: GRADE_PARTICLE_COLORS.SS },
      S: { count: 900, size: 0.07, color: GRADE_PARTICLE_COLORS.S },
      A: { count: 700, size: 0.06, color: GRADE_PARTICLE_COLORS.A },
      B: { count: 600, size: 0.05, color: GRADE_PARTICLE_COLORS.B },
      C: { count: 500, size: 0.04, color: GRADE_PARTICLE_COLORS.C },
      D: { count: 400, size: 0.03, color: GRADE_PARTICLE_COLORS.D },
    };
    
    const baseConfig = baseConfigs[highestGrade];
    return {
      ...baseConfig,
      count: getOptimalParticleCount(baseConfig.count), // 모바일 최적화 적용
    };
  }, [highestGrade]);

  const particles = useMemo(() => {
    return generateSpherePoints(particleConfig.count, 3);
  }, [particleConfig.count]);

  useFrame((state: any, delta: number) => {
    if (!pointsRef.current) return;

    if (phase === 'reveal' || phase === 'highlight') {
      // 등급이 높을수록 더 빠르게 회전
      const rotationSpeed = highestGrade === 'SS' ? 0.3 : highestGrade === 'S' ? 0.2 : 0.15;
      pointsRef.current.rotation.x += delta * rotationSpeed;
      pointsRef.current.rotation.y += delta * (rotationSpeed * 1.5);
    }
  });

  if (phase === 'complete') return null;

  return (
    <Points ref={pointsRef} positions={particles} stride={3} frustumCulled={false}>
      <PointMaterial
        transparent
        color={particleConfig.color}
        size={particleConfig.size}
        sizeAttenuation={true}
        depthWrite={false}
      />
    </Points>
  );
}

